import React, { FC, useEffect, useState } from 'react';
import { SortSelector } from '@/components/SortSelector';
import { DMSortType, SortDirection, SortOption, SortType } from '@/declarations/models/SortOption';
import Layout from '../../Layout';
import MediaList from '../../MediaList/MediaList';
import { ViewType } from '../../../declarations/ViewType';
import FinderLayout from '../FinderLayout';
import { DMMediaModel } from '../../../declarations/models/DMMediaModel';
import { GenericMedia } from '../../../declarations/GenericMedia';
import { toggleItem } from '../../../utils/array';
import { Api } from '../../../services/Api';
import { MediaLicense } from '../../../declarations/models/MediaLicense';
import { Settings } from '../../../Settings';
import SelectedDMMediaItem from './SelectedDMMediaItem';
import { DMObjectType } from '../../../declarations/models/DMObjectType';
import DMFinderHeader from './DMFinderHeader';
import DMImagePicker from './DMImagePicker';

export interface DMMediaFinderProps {
  open: boolean;
  onClose: () => void;
  onSelectionConfirmed: (selectedItems: Array<GenericMedia<DMMediaModel>>) => void;
  multiSelect?: boolean;
  objectType?: DMObjectType;
  hasLocationDep?: boolean;
  hasVideoDep?: boolean;
  hasAudioDep?: boolean;
  /**
   * Pick images rather than objects. An object with more than one image opens a step where its images can be picked,
   * and each picked image is returned as the object with that image as its main image.
   */
  pickImages?: boolean;
}

export const DMMediaFinder: FC<DMMediaFinderProps> = ({
  open,
  onClose,
  onSelectionConfirmed,
  multiSelect = true,
  objectType,
  hasLocationDep,
  hasVideoDep,
  hasAudioDep,
  pickImages = false,
}) => {
  const [selectedLicense, setSelectedLicense] = useState<MediaLicense | null>(null);
  const [hasLocation, setHasLocation] = useState<boolean>(!!hasLocationDep);
  const [hasVideo, setHasVideo] = useState<boolean>(!!hasVideoDep);
  const [hasAudio, setHasAudio] = useState<boolean>(!!hasAudioDep);
  const [hasPictures, setHasPictures] = useState<boolean>(false);
  const [sortOption, setSortOption] = useState<SortOption>({
    sortBy: SortType.RELEVANCE,
    order: SortDirection.DESC,
  });

  useEffect(() => setHasLocation(!!hasLocationDep), [hasLocationDep]);
  useEffect(() => setHasVideo(!!hasVideoDep), [hasVideoDep]);
  useEffect(() => setHasAudio(!!hasAudioDep), [hasAudioDep]);

  const [page, setPage] = useState<{ start: number; rows: number }>({ start: 0, rows: 20 });
  const resetPage = () => {
    setPage((prevPage) => ({ ...prevPage, start: 0 }));
  };
  const [selectedItems, setSelectedItems] = useState<Array<GenericMedia<DMMediaModel>>>([]);
  const [mediaItems, setMediaItems] = useState<Array<GenericMedia<DMMediaModel>>>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [imagePickerItem, setImagePickerItem] = useState<GenericMedia<DMMediaModel> | null>(null);
  const [currentOwner, _setCurrentOwner] = useState<string>('');
  const setCurrentOwner: typeof _setCurrentOwner = (owner) => {
    resetPage();
    _setCurrentOwner(owner);
  };
  const [selectedObjectType, _setSelectedObjectType] = useState<DMObjectType | null>(null);
  const setSelectedObjectType: typeof _setSelectedObjectType = (type) => {
    resetPage();
    _setSelectedObjectType(type);
  };
  const [queryString, _setQueryString] = useState<string>('');
  const setQueryString: typeof _setQueryString = (query) => {
    resetPage();
    _setQueryString(query);
  };

  const resetState = () => {
    setSelectedItems([]);
    setQueryString('');
    setSelectedLicense(null);
    setHasLocation(!!hasLocationDep);
    setHasVideo(!!hasVideoDep);
    setHasAudio(!!hasAudioDep);
    setHasPictures(!hasAudioDep);
    setSortOption({
      sortBy: SortType.RELEVANCE,
      order: SortDirection.DESC,
    });
    setCurrentOwner('');
    setSelectedObjectType(null);
  };

  const _onClose = () => {
    onClose();
    resetState();
  };

  const isSameObject = (a: GenericMedia<DMMediaModel>, b: GenericMedia<DMMediaModel>) =>
    a.source?.['artifact.uuid'] === b.source?.['artifact.uuid'];

  const hasImagesToPick = (item: GenericMedia<DMMediaModel>) =>
    pickImages && (item.source?.['artifact.pictureCount'] ?? 0) > 1;

  const isSelected = (item: GenericMedia<DMMediaModel>): boolean => {
    if (hasImagesToPick(item)) return selectedItems.some((i) => isSameObject(i, item));
    return selectedItems.some((i) => i.id === item.id);
  };

  const handleRemoveSelected = (item: GenericMedia<DMMediaModel>) => {
    setSelectedItems((selected) => selected.filter((i) => i.id !== item.id));
  };

  const handleImagesPicked = (item: GenericMedia<DMMediaModel>, images: Array<GenericMedia<DMMediaModel>>) => {
    setSelectedItems((selected) =>
      multiSelect ? [...selected.filter((i) => !isSameObject(i, item)), ...images] : images.slice(0, 1),
    );
    setImagePickerItem(null);
  };

  const handleSelectionChanged = (item: GenericMedia<DMMediaModel>) => {
    if (hasImagesToPick(item)) {
      setImagePickerItem(item);
    } else if (multiSelect) {
      setSelectedItems((selected) => toggleItem(selected, item, (a, b) => a.id === b.id));
    } else {
      setSelectedItems((previouslySelected) =>
        previouslySelected.length === 1 && previouslySelected[0].id === item.id ? [] : [item],
      );
    }
  };

  const handlePageChanged = (changes: Partial<typeof page>) => {
    setPage((prevPage) => ({ ...prevPage, ...changes }));
  };

  const handleSelectionConfirmed: DMMediaFinderProps['onSelectionConfirmed'] = (selected) => {
    _onClose();
    setSelectedItems([]);
    onSelectionConfirmed(selected);
  };

  const sortTypeToDMSortType = (sortType: SortType): DMSortType => {
    switch (sortType) {
      case SortType.TITLE:
        return DMSortType.TITLE;
      case SortType.UPDATED_AT:
        return DMSortType.UPDATED_AT;
      case SortType.RELEVANCE:
        return DMSortType.RELEVANCE;
      default:
        return DMSortType.RELEVANCE;
    }
  };

  useEffect(() => {
    if (!open) return;

    let unmounted = false;
    setIsLoading(true);

    const type = objectType || selectedObjectType;

    const ctx = Api.getDMSearchResult({
      ...page,
      query: queryString,
      licenses: selectedLicense !== null ? [selectedLicense] : undefined,
      owners: currentOwner || undefined,
      types: type ? [type] : undefined,
      has_location: hasLocation || undefined,
      has_pictures: hasPictures || undefined,
      has_video: hasVideo || undefined,
      has_audio: hasAudio || undefined,
      order_by: sortTypeToDMSortType(sortOption.sortBy),
      order: sortOption.order,
    });
    ctx
      .fetchDirect(null)
      .then((loadedPage) => {
        if (!unmounted) {
          setTotalCount(loadedPage?.totalCount || 0);
          setMediaItems(
            (loadedPage?.items || []).map(
              (item) =>
                ({
                  id: item['artifact.uuid'],
                  title: item['artifact.ingress.title'],
                  url: item['artifact.defaultMediaIdentifier']
                    ? `${Settings.DM_HOST}/image/${item['artifact.defaultMediaIdentifier']}`
                    : '',
                  license: item['artifact.ingress.license']?.[0] || MediaLicense.NONE,
                  source: item,
                } as GenericMedia<DMMediaModel>),
            ),
          );
        }
      })
      .finally(() => {
        if (!unmounted) setIsLoading(false);
      });
    return () => {
      unmounted = true;
      ctx.abort();
    };
  }, [
    queryString,
    selectedLicense,
    currentOwner,
    selectedObjectType,
    page,
    open,
    objectType,
    hasLocation,
    hasPictures,
    hasVideo,
    hasAudio,
    sortOption,
  ]);

  return (
    <FinderLayout<DMMediaModel>
      open={open}
      selectedItems={selectedItems}
      renderSelectedItem={(item) => <SelectedDMMediaItem item={item} onRemoveItem={handleRemoveSelected} />}
      onConfirmSelection={handleSelectionConfirmed}
      onClose={_onClose}>
      <Layout
        headerContent={
          <DMFinderHeader
            // queryString={queryString}
            mediaLicense={selectedLicense}
            selectedOwnerCode={currentOwner}
            selectedObjectType={selectedObjectType}
            onQueryChanged={setQueryString}
            onMediaLicenseChanged={setSelectedLicense}
            onOwnerCodeChanged={setCurrentOwner}
            onObjectTypeChanged={setSelectedObjectType}
            objectTypeSelectable={!objectType}
            hasLocation={hasLocation}
            setHasLocation={setHasLocation}
            hasPictures={hasPictures}
            setHasPictures={setHasPictures}
            hasVideo={hasVideo}
            setHasVideo={setHasVideo}
            hasAudio={hasAudio}
            setHasAudio={setHasAudio}
          />
        }>
        <MediaList
          isLoading={isLoading}
          items={mediaItems}
          page={page}
          totalItemCount={totalCount}
          onPageChange={(_, start, rows) => handlePageChanged({ start, rows })}
          onPageSizeChange={(rows) => handlePageChanged({ rows })}
          defaultSelectedViewType={ViewType.GRID}
          getIsSelected={isSelected}
          onSelectChanged={handleSelectionChanged}
          isSelectable
          // TODO: Implement favouriteable
          isFavouriteable={false}
          variant='dm'
          customHeaderElement={
            <SortSelector
              availableOptions={[SortType.TITLE, SortType.UPDATED_AT, SortType.RELEVANCE]}
              sortOption={sortOption}
              setSortOption={setSortOption}
            />
          }
        />
      </Layout>
      {pickImages && (
        <DMImagePicker
          item={imagePickerItem}
          selectedItems={selectedItems}
          multiSelect={multiSelect}
          onClose={() => setImagePickerItem(null)}
          onConfirm={handleImagesPicked}
        />
      )}
    </FinderLayout>
  );
};

export default DMMediaFinder;
